import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Scatter, ScatterChart, ZAxis
} from 'recharts';
import { 
  Package, Truck, Warehouse, Globe, Route, Clock, AlertTriangle, 
  CheckCircle2, Zap, Target, Users, DollarSign, ArrowUpRight, 
  ArrowDownRight, MapPin, Navigation, Signal, Wifi, Radio
} from 'lucide-react';

const OverviewTab = ({ timeRange }) => {
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [isMapVisible, setIsMapVisible] = useState(true);

  // Mock data for charts
  const performanceData = [
    { month: 'Jan', orders: 1200, efficiency: 85, cost: 45000, delivery: 92 },
    { month: 'Feb', orders: 1350, efficiency: 87, cost: 48000, delivery: 94 },
    { month: 'Mar', orders: 1100, efficiency: 82, cost: 42000, delivery: 89 },
    { month: 'Apr', orders: 1600, efficiency: 90, cost: 52000, delivery: 96 },
    { month: 'May', orders: 1400, efficiency: 88, cost: 47000, delivery: 93 },
    { month: 'Jun', orders: 1800, efficiency: 92, cost: 58000, delivery: 97 }
  ];

  const logisticsData = [
    { name: 'Air Freight', value: 25, color: '#3B82F6' },
    { name: 'Sea Freight', value: 35, color: '#10B981' },
    { name: 'Road Transport', value: 30, color: '#F59E0B' },
    { name: 'Rail', value: 10, color: '#8B5CF6' }
  ];

  const networkNodes = [
    { id: 1, name: 'Riyadh Hub', x: 50, y: 30, status: 'active', connections: 8, type: 'hub' },
    { id: 2, name: 'Jeddah Port', x: 20, y: 70, status: 'active', connections: 5, type: 'port' },
    { id: 3, name: 'Dammam DC', x: 80, y: 60, status: 'active', connections: 6, type: 'warehouse' },
    { id: 4, name: 'Makkah Store', x: 30, y: 40, status: 'warning', connections: 3, type: 'store' },
    { id: 5, name: 'Medina Hub', x: 70, y: 20, status: 'active', connections: 4, type: 'hub' },
    { id: 6, name: 'Abha DC', x: 40, y: 80, status: 'inactive', connections: 2, type: 'warehouse' }
  ];

  const realTimeAlerts = [
    { id: 1, type: 'warning', message: 'Low stock alert: Item #SKU-1234', time: '2 min ago', priority: 'medium' },
    { id: 2, type: 'error', message: 'Delivery delay: Order #ORD-5678', time: '5 min ago', priority: 'high' },
    { id: 3, type: 'success', message: 'Shipment arrived: Port Jeddah', time: '8 min ago', priority: 'low' },
    { id: 4, type: 'info', message: 'New order received: #ORD-9012', time: '12 min ago', priority: 'low' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'inactive': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      case 'success': return <CheckCircle2 className="w-4 h-4" />;
      case 'info': return <Signal className="w-4 h-4" />;
      default: return <Signal className="w-4 h-4" />;
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'info': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="p-8 space-y-8">
      {/* Real-time Alerts */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="grid grid-cols-1 lg:grid-cols-4 gap-6"
      >
        {realTimeAlerts.map((alert, index) => (
          <motion.div
            key={alert.id}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            className={`p-4 rounded-2xl border ${getAlertColor(alert.type)} backdrop-blur-sm`}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {getAlertIcon(alert.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{alert.message}</p>
                <p className="text-xs opacity-75 mt-1">{alert.time}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Main Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Performance Overview */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Performance Overview</h3>
              <p className="text-gray-600">Key metrics over time</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
            </div>
          </div>
          
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: 'none', 
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="orders" 
                stackId="1" 
                stroke="#3B82F6" 
                fill="#3B82F6" 
                fillOpacity={0.3}
              />
              <Line 
                type="monotone" 
                dataKey="efficiency" 
                stroke="#10B981" 
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
              />
              <Bar dataKey="delivery" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Logistics Distribution */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Logistics Distribution</h3>
              <p className="text-gray-600">Transportation methods breakdown</p>
            </div>
            <Truck className="w-6 h-6 text-blue-600" />
          </div>
          
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={logisticsData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {logisticsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: 'none', 
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          
          <div className="grid grid-cols-2 gap-4 mt-6">
            {logisticsData.map((item, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.name}</p>
                  <p className="text-xs text-gray-600">{item.value}%</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Network Visualization */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Network Topology</h3>
            <p className="text-gray-600">Real-time network status and connections</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsMapVisible(!isMapVisible)}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
            >
              {isMapVisible ? 'Hide Map' : 'Show Map'}
            </button>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Regions</option>
              <option value="central">Central</option>
              <option value="western">Western</option>
              <option value="eastern">Eastern</option>
            </select>
          </div>
        </div>

        <div className="relative h-96 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl overflow-hidden">
          {/* Network Connections */}
          <svg className="absolute inset-0 w-full h-full">
            {networkNodes.map((node, index) => 
              networkNodes.slice(index + 1).map((target, targetIndex) => (
                <motion.line
                  key={`${node.id}-${target.id}`}
                  x1={`${node.x}%`}
                  y1={`${node.y}%`}
                  x2={`${target.x}%`}
                  y2={`${target.y}%`}
                  stroke="#3B82F6"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                />
              ))
            )}
          </svg>

          {/* Network Nodes */}
          {networkNodes.map((node, index) => (
            <motion.div
              key={node.id}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="absolute transform -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${node.x}%`, top: `${node.y}%` }}
            >
              <div className="relative group">
                <div className={`
                  w-12 h-12 rounded-full border-4 border-white shadow-lg flex items-center justify-center
                  ${getStatusColor(node.status)} relative
                `}>
                  {node.type === 'hub' && <Globe className="w-6 h-6 text-white" />}
                  {node.type === 'port' && <Navigation className="w-6 h-6 text-white" />}
                  {node.type === 'warehouse' && <Warehouse className="w-6 h-6 text-white" />}
                  {node.type === 'store' && <Package className="w-6 h-6 text-white" />}
                  
                  {/* Pulse animation for active nodes */}
                  {node.status === 'active' && (
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-green-400"
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.8, 0, 0.8]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  )}
                </div>
                
                {/* Node Info Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap">
                    <div className="font-medium">{node.name}</div>
                    <div className="text-xs opacity-75">{node.connections} connections</div>
                  </div>
                  <div className="w-2 h-2 bg-gray-900 transform rotate-45 absolute top-full left-1/2 -translate-x-1/2 -mt-1"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Network Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          {[
            { label: 'Active Nodes', value: '5', color: 'text-green-600' },
            { label: 'Total Connections', value: '28', color: 'text-blue-600' },
            { label: 'Network Load', value: '78%', color: 'text-orange-600' },
            { label: 'Response Time', value: '2.3ms', color: 'text-purple-600' }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
              className="text-center p-4 bg-white/50 rounded-xl"
            >
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        {[
          { title: 'Create Order', icon: Package, color: 'from-blue-500 to-cyan-500', action: () => {} },
          { title: 'Track Shipment', icon: Truck, color: 'from-emerald-500 to-teal-500', action: () => {} },
          { title: 'Inventory Check', icon: Warehouse, color: 'from-purple-500 to-pink-500', action: () => {} }
        ].map((action, index) => (
          <motion.button
            key={action.title}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={action.action}
            className="group p-6 bg-white/60 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 hover:bg-white/80 transition-all duration-300"
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${action.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
              <action.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{action.title}</h3>
            <p className="text-gray-600 text-sm">Quick access to common actions</p>
          </motion.button>
        ))}
      </motion.div>
    </div>
  );
};

export default OverviewTab; 